import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { Loader2, Upload, X, Image as ImageIcon } from 'lucide-react'
import { Course, CreateCourseDTO, UpdateCourseDTO } from '@/services/admin/courses.service'
import { useCompanies } from '@/services/admin/companies.service'
import { useMentors } from '@/services/super-admin/mentors.service'
import { useUploadThumbnail } from '@/services/upload.service'
import { useAuth } from '@/hooks/use-auth'
import { isSuperAdmin } from '@/types/auth'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'

const courseFormSchema = z
  .object({
    title: z.string().min(3, 'Título deve ter no mínimo 3 caracteres').max(200),
    description: z.string().min(1, 'Descrição é obrigatória'),
    slug: z
      .string()
      .min(3)
      .max(100)
      .regex(
        /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
        'Slug deve conter apenas letras minúsculas, números e hifens'
      ),
    type: z.enum(['regular', 'masterclass', 'workshop', 'trail']).default('regular'),
    accessType: z.enum(['free', 'restricted', 'paid']).default('free'),
    level: z.enum(['beginner', 'intermediate', 'advanced', 'expert']).default('beginner'),
    status: z.enum(['draft', 'published', 'archived', 'coming_soon']).default('draft'),
    category: z.string().optional(),
    companyId: z.string().nullable().optional(),
    mentorId: z.string().nullable().optional(),
    thumbnail: z.string().url('URL inválida').optional().or(z.literal('')),
    trailer: z.string().url('URL inválida').optional().or(z.literal('')),
    order: z.coerce.number().int().min(0).default(0),
    duration: z.coerce.number().int().positive().optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'masterclass' && !data.mentorId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mentorId'],
        message: 'Selecione um mentor pra masterclass',
      })
    }
  })

type CourseFormValues = z.infer<typeof courseFormSchema>

interface CourseFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  course?: Course | null
  onSubmit: (data: CreateCourseDTO | UpdateCourseDTO) => Promise<void>
  isSubmitting?: boolean
}

export function CourseFormDialog({
  open,
  onOpenChange,
  course,
  onSubmit,
  isSubmitting = false,
}: CourseFormDialogProps) {
  const { toast } = useToast()
  const { user } = useAuth()
  const userIsSuperAdmin = isSuperAdmin(user?.role)
  const { data: companies } = useCompanies(userIsSuperAdmin)
  // Mentores carregados sempre — dropdown só aparece quando type=masterclass,
  // mas o fetch fica ativo pra não dar flash quando o user troca o tipo.
  const { data: mentors } = useMentors({ activeOnly: true, enabled: userIsSuperAdmin })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('')
  const { mutate: uploadThumbnail, isPending: isUploadingThumbnail } = useUploadThumbnail()

  const form = useForm<CourseFormValues>({
    resolver: zodResolver(courseFormSchema),
    defaultValues: {
      title: '',
      description: '',
      slug: '',
      type: 'regular',
      accessType: 'free',
      level: 'beginner',
      status: 'draft',
      category: '',
      companyId: null,
      mentorId: null,
      thumbnail: '',
      trailer: '',
      order: 0,
      duration: '',
    },
  })

  // Reset form when course changes or dialog opens
  useEffect(() => {
    if (open) {
      if (course) {
        setThumbnailPreview(course.thumbnail || '')
        form.reset({
          title: course.title,
          description: course.description,
          slug: course.slug,
          type: course.type,
          accessType: course.accessType,
          level: course.level,
          status: course.status,
          category: course.category || '',
          companyId: course.companyId || null,
          mentorId: course.mentorId || null,
          thumbnail: course.thumbnail || '',
          trailer: course.trailer || '',
          order: course.order,
          duration: course.duration || '',
        })
      } else {
        setThumbnailPreview('')
        form.reset({
          title: '',
          description: '',
          slug: '',
          type: 'regular',
          accessType: 'free',
          level: 'beginner',
          status: 'draft',
          category: '',
          companyId: null,
          mentorId: null,
          thumbnail: '',
          trailer: '',
          order: 0,
          duration: '',
        })
      }
    }
  }, [course, open, form])

  const handleThumbnailClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validação client-side
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Formato inválido',
          description: 'Por favor, selecione um arquivo de imagem.',
          variant: 'destructive',
        })
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Arquivo muito grande',
          description: 'A imagem deve ter no máximo 10MB.',
          variant: 'destructive',
        })
        return
      }

      // Preview local
      const localUrl = URL.createObjectURL(file)
      setThumbnailPreview(localUrl)

      // Upload real
      uploadThumbnail(file, {
        onSuccess: (url) => {
          form.setValue('thumbnail', url)
          setThumbnailPreview(url)
          toast({
            title: 'Thumbnail enviada!',
            description: 'Imagem redimensionada para 1024x1024px',
          })
          URL.revokeObjectURL(localUrl)
        },
        onError: (error: unknown) => {
          setThumbnailPreview('')
          toast({
            title: 'Erro ao enviar thumbnail',
            description: getErrorMessage(error, 'Tente novamente mais tarde.'),
            variant: 'destructive',
          })
          URL.revokeObjectURL(localUrl)
        },
      })
    }
  }

  const handleRemoveThumbnail = () => {
    form.setValue('thumbnail', '')
    setThumbnailPreview('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (values: CourseFormValues) => {
    // Partial pra permitir tanto Create (campos required) quanto Update (tudo opcional).
    // companyId é tratado fora do values pra ser removido em admin não-super.
    const data: Partial<CreateCourseDTO> & {
      companyId?: string | null
      mentorId?: string | null
    } = {
      ...values,
      category: values.category || undefined,
      thumbnail: values.thumbnail || undefined,
      trailer: values.trailer || undefined,
      duration: values.duration ? Number(values.duration) : undefined,
      // mentorId só faz sentido quando type=masterclass. Em outros tipos
      // mandamos null pra desvincular (caso o admin tenha mudado o tipo
      // do curso de masterclass pra outro).
      mentorId: values.type === 'masterclass' ? values.mentorId || null : null,
    }

    // Só envia companyId se for super_admin
    if (userIsSuperAdmin) {
      data.companyId = values.companyId || null
    } else {
      delete data.companyId
    }

    await onSubmit(data)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{course ? 'Editar Curso' : 'Novo Curso'}</DialogTitle>
          <DialogDescription>
            {course ? 'Atualize as informações do curso' : 'Preencha os dados do novo curso'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Título *</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome do curso" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Slug *</FormLabel>
                    <FormControl>
                      <Input placeholder="curso-introducao-ia" {...field} />
                    </FormControl>
                    <FormDescription>
                      URL amigável (apenas letras minúsculas, números e hifens)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Descrição *</FormLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Descrição completa do curso (suporta formatação)"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="regular">Regular</SelectItem>
                        <SelectItem value="masterclass">Masterclass</SelectItem>
                        <SelectItem value="workshop">Workshop</SelectItem>
                        <SelectItem value="trail">Trilha</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Mentor — só aparece quando type=masterclass. Obrigatório
                  (validação via superRefine no schema). */}
              {form.watch('type') === 'masterclass' && (
                <FormField
                  control={form.control}
                  name="mentorId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mentor *</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v || null)}
                        value={field.value ?? ''}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um mentor" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {!mentors || mentors.length === 0 ? (
                            <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                              Nenhum mentor cadastrado.
                              <br />
                              Cadastre em <em>Super Admin → Mentores</em>.
                            </div>
                          ) : (
                            mentors.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name}
                                {m.role || m.company ? (
                                  <span className="text-muted-foreground">
                                    {' · '}
                                    {[m.role, m.company].filter(Boolean).join(' · ')}
                                  </span>
                                ) : null}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nível</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o nível" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="beginner">Iniciante</SelectItem>
                        <SelectItem value="intermediate">Intermediário</SelectItem>
                        <SelectItem value="advanced">Avançado</SelectItem>
                        <SelectItem value="expert">Expert</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="accessType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Acesso</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Tipo de acesso" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="free">Gratuito</SelectItem>
                        <SelectItem value="restricted">Restrito</SelectItem>
                        <SelectItem value="paid">Pago</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Status do curso" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft">Rascunho</SelectItem>
                        <SelectItem value="published">Publicado</SelectItem>
                        <SelectItem value="archived">Arquivado</SelectItem>
                        <SelectItem value="coming_soon">Em Breve</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {userIsSuperAdmin && (
                <>
                  <FormField
                    control={form.control}
                    name="companyId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Empresa</FormLabel>
                        <Select
                          onValueChange={(v) => field.onChange(v === '__none__' ? null : v)}
                          value={field.value || '__none__'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a empresa" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="__none__">Nenhuma (global)</SelectItem>
                            {companies?.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>Empresa dona deste curso</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <FormControl>
                      <Input placeholder="IA, Desenvolvimento, Design..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ordem</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duração (minutos)</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" placeholder="120" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="thumbnail"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Thumbnail/Ícone do Curso</FormLabel>
                    <FormControl>
                      <div className="space-y-4">
                        {/* Preview da thumbnail */}
                        {thumbnailPreview ? (
                          <div className="relative w-full max-w-sm">
                            <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-border bg-muted">
                              <img
                                src={thumbnailPreview}
                                alt="Preview da thumbnail"
                                className="w-full h-full object-cover"
                              />
                              {isUploadingThumbnail && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute -top-2 -right-2"
                              onClick={handleRemoveThumbnail}
                              disabled={isUploadingThumbnail}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div
                            onClick={handleThumbnailClick}
                            className="w-full max-w-sm aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary transition-colors cursor-pointer bg-muted/50 hover:bg-muted flex flex-col items-center justify-center gap-2 group"
                          >
                            <div className="p-4 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                              <ImageIcon className="w-8 h-8 text-primary" />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-medium text-foreground">
                                Clique para fazer upload
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                JPG, PNG ou WebP (máx. 10MB)
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Será redimensionada para 1024x1024px
                              </p>
                            </div>
                            <Upload className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                        )}

                        {/* Input file oculto */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFileChange}
                          className="hidden"
                          disabled={isUploadingThumbnail}
                        />

                        {/* Input de URL manual (alternativa) */}
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground">
                            Ou insira uma URL manualmente:
                          </p>
                          <Input
                            placeholder="https://..."
                            {...field}
                            onChange={(e) => {
                              field.onChange(e)
                              setThumbnailPreview(e.target.value)
                            }}
                            disabled={isUploadingThumbnail}
                          />
                        </div>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Faça upload ou insira a URL da imagem/ícone do curso (1024x1024px).
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="trailer"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>URL do Trailer</FormLabel>
                    <FormControl>
                      <Input placeholder="https://..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {course ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
